import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { BookHeart, Trash2 } from "lucide-react";

export default function Memories() {
  const t = useT();
  const qc = useQueryClient();
  const [text, setText] = useState("");

  const q = useQuery({
    queryKey: ["memories"],
    queryFn: async () => (await api.get("/memories")).data,
    retry: false,
  });
  const settings = useQuery({
    queryKey: ["memory-settings"],
    queryFn: async () => (await api.get("/memory-settings")).data,
    retry: false,
  });

  const items = Array.isArray(q.data) ? q.data : q.data?.items || [];
  const enabled = !!(settings.data?.enabled ?? settings.data?.memory_enabled ?? true);

  const addMem = async () => {
    if (!text.trim()) return;
    try {
      await api.post("/memories", { text: text.trim(), content: text.trim() });
      setText("");
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["memories"] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!id) return;
    try {
      await api.delete(`/memories/${id}`);
      qc.invalidateQueries({ queryKey: ["memories"] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (v) => {
    try {
      await api.patch("/memory-settings", { enabled: v, memory_enabled: v });
      qc.invalidateQueries({ queryKey: ["memory-settings"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("nav.memories")} title={t("client.yourMemories")} subtitle={t("brand.tagline")} />

      <div className="rounded-3xl bg-white shadow-card p-4 md:p-6 flex items-center justify-between gap-3" data-testid="memory-settings">
        <div>
          <div className="font-heading text-ink-900">{t("nav.memories")}</div>
          <div className="text-sm text-ink-500">Companion may remember important things.</div>
        </div>
        <Switch checked={enabled} onCheckedChange={toggle} data-testid="memory-toggle" />
      </div>

      <div className="rounded-3xl bg-white shadow-card p-4 md:p-6 flex gap-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addMem(); }}
          placeholder="Something worth remembering…"
          className="rounded-xl bg-cream-50 h-11"
          data-testid="memory-input"
        />
        <Button onClick={addMem} className="rounded-full bg-sage-500 hover:bg-sage-600 h-11 px-5" data-testid="memory-add">
          {t("common.add")}
        </Button>
      </div>

      {q.isLoading && <LoadingCard height="h-24" />}
      {q.isError && <ErrorState message={errMsg(q.error)} onRetry={() => q.refetch()} />}
      {!q.isLoading && !q.isError && items.length === 0 && (
        <EmptyState icon={BookHeart} title={t("common.empty")} testId="memories-empty" />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="memories-list">
        {items.map((m, i) => (
          <div key={m.id || i} className="rounded-2xl bg-white shadow-card p-4 flex items-start gap-3">
            <BookHeart className="h-4 w-4 text-sage-600 mt-1" strokeWidth={1.5} />
            <div className="flex-1">
              <div className="text-ink-900 text-sm">{m.text || m.content || m.summary}</div>
              {m.created_at && <div className="text-xs text-ink-500 mt-1">{new Date(m.created_at).toLocaleDateString()}</div>}
            </div>
            {m.id && (
              <button
                onClick={() => del(m.id)}
                data-testid={`memory-delete-${i}`}
                className="text-ink-500 hover:text-rust-600"
                aria-label="delete"
              >
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
